import React from 'react';
import CsvExample from '../../assets/CSV_example.jpg';

import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPaintbrush, faUpload, faFileImage, faFilePen, faListCheck, faIdBadge, faClone, faSliders, faLayerGroup,
    faArrowDownLong, faRulerCombined, faBook, faFileLines, faHandPointer, faLink, faLightbulb, faFileInvoice, faStar, faDraftingCompass,
    faFont, faPalette, faBookOpen, faImage, faPenNib, faFilm, faCheckCircle, faFolderOpen, faFileArchive, faFileZipper, faTable, faFolder, faTag,
    faArrowsAltH, faCode, faBan, faSitemap, faDatabase
} from '@fortawesome/free-solid-svg-icons';

import ArticleLayout from '../../components/ArticleLayout/ArticleLayout';
import { colorBrewer } from 'react-syntax-highlighter/dist/esm/styles/hljs';

const ArticleDynamicCreative = () => {
    return (
        <ArticleLayout
            title="Dynamic Creative Submission Guidelines"
            subtitle=""
        >
            <Helmet>
                <title>Dynamic Creative Submission Guidelines</title>
            </Helmet>

            <p>
                Designing assets for dynamic display requires careful attention to ensure the design remains flexible for dynamic content.
                The main objective is to enable ads to be personalized in real time, adapting to consumer characteristics, behaviors,
                and circumstances.
            </p>
            <p>
                To create the best experience, minimize delays, and streamline production, please review the guidelines below.
                They outline the requirements for delivering all creative assets based on your current status with Dynamic Creative Ads.
            </p>

            {/* Sumário / Navegação rápida */}
            <div className="button-container">
                <a href="#start-from-scratch" style={{ textDecoration: 'none' }}>
                    <div className="button-box">Start from Scratch</div>
                </a>
                <a href="#mockup-ready" style={{ textDecoration: 'none' }}>
                    <div className="button-box">Mockup Ready</div>
                </a>
                <a href="#all-versions-ready" style={{ textDecoration: 'none' }}>
                    <div className="button-box">All Versions and Sizes Ready</div>
                </a>
                <a href="#versions-through-feed" style={{ textDecoration: 'none' }}>
                    <div className="button-box">Versions Through Feed</div>
                </a>
            </div> <br />

            <hr />

            {/* Seção 1 */}
            <h2 id="start-from-scratch" className="article-section-title">
                <FontAwesomeIcon icon={faLightbulb} /> Start from Scratch
            </h2>
            <p>
                If you are planning to start a <strong>Dynamic Creative Optimization (DCO) </strong>
                campaign but have no creative ads designed yet, our team can develop a proposal from
                scratch. We can utilize elements from your website, previous campaigns, or any creative
                footage you wish to use.
            </p>

            <br />

            <p> It is crucial to share as much information and as many assets as possible, such as: </p>
            <ul>
                <li>
                    <FontAwesomeIcon icon={faFileInvoice} className="icon-secondary-article" /> Landing pages for the campaign, including links and destinations.
                </li>
                <li>
                    <FontAwesomeIcon icon={faStar} className="icon-secondary-article" /> Logos in <strong>PNG or SVG</strong> format.
                </li>
                <li>
                    <FontAwesomeIcon icon={faFont} className="icon-secondary-article" /> The font file (<strong>TTF, OTF</strong>) if a corporate or official font is required.
                </li>
                <li>
                    <FontAwesomeIcon icon={faPalette} className="icon-secondary-article" /> Official brand colors, preferably in <strong>RGB or HEX</strong> format.
                </li>
                <li>
                    <FontAwesomeIcon icon={faBookOpen} className="icon-secondary-article" /> Any official brand guidelines and instructions.
                </li>
            </ul>

            <br />

            <p style={{ color: '#000443' }}><strong>Our team will develop a proposal using the assets provided, including:</strong></p>
            <ul>
                <li>
                    <FontAwesomeIcon icon={faImage} className="icon-secondary-article" /> <strong>Images and Graphics:</strong> Accepted formats are <strong>PNG, JPG, and SVG</strong>.
                </li>
                <li>
                    <FontAwesomeIcon icon={faPenNib} className="icon-secondary-article" /> <strong>Text and Copy:</strong> The copy that should appear on the banner.
                </li>
                <li>
                    <FontAwesomeIcon icon={faHandPointer} className="icon-secondary-article" /> <strong>Interactive Elements:</strong> Such as a Call-to-Action (<strong>CTA</strong>).
                </li>
                <li>
                    <FontAwesomeIcon icon={faFilm} className="icon-secondary-article" /> <strong>Animations:</strong> Transitions, effects, or element animations for optimal performance.
                </li>
                <li>
                    <FontAwesomeIcon icon={faLayerGroup} className="icon-secondary-article" /> <strong>Examples of different versions</strong> based on the campaign strategy.
                </li>
            </ul>

            <div className="note">
                <p><strong>Note:</strong> If there is any additional information our team should consider,
                    please include it in your request. After reviewing the request, our team will provide a
                    timeline for revisions and final approval. Once the initial mockup is approved, our team
                    will create the different versions and sizes needed for the DCO campaign.</p>
            </div>

            <br />

            {/* Seção 2 */}
            <h2 id="mockup-ready" className="article-section-title">
                <FontAwesomeIcon icon={faPaintbrush} className="icon-secondary" /> Mockup Ready
            </h2>
            <p>
                This guide is for those who want to start a DCO campaign and already have a key visual or a PSD mockup.
                You may want our team to replicate this ad in different sizes and develop all the different versions according
                to your campaign strategy. To avoid errors and delays, it is essential to share the assets correctly.
            </p>
            <h3>
                <FontAwesomeIcon icon={faUpload} /> Please provide your mockup according to the follow guidelines:
            </h3>
            <ul>
                <li><FontAwesomeIcon icon={faFileImage} className="icon-secondary-article" /> <strong>PSD file (Photoshop)</strong> with all layers editable.</li>
                <li><FontAwesomeIcon icon={faFilePen} className="icon-secondary-article" /> <strong>AI file (Illustrator)</strong> with all images included in a separate folder.</li>
                <li><FontAwesomeIcon icon={faListCheck} className="icon-secondary-article" /> All layers must be named correctly (e.g., <code>Background</code>, <code>Product_img</code>, <code>Headline</code>).</li>
                <li><FontAwesomeIcon icon={faIdBadge} className="icon-secondary-article" /> Official branding elements, such as logos and slogans, must be editable or shared in a separate folder.</li>
                <li><FontAwesomeIcon icon={faClone} className="icon-secondary-article" /> If the ad contains several frames, it is important to share a clear visual of each frame.</li>
                <li><FontAwesomeIcon icon={faFont} className="icon-secondary-article" /> The corporate official font file (<strong>TTF, OTF</strong>) is required.</li>
                <li><FontAwesomeIcon icon={faPalette} className="icon-secondary-article" /> Official brand colors, preferably in <strong>RGB or HEX</strong> format.</li>
                <li><FontAwesomeIcon icon={faSliders} className="icon-secondary-article" /> Directions for preferred transitions, effects, or element animations.</li>
                <li><FontAwesomeIcon icon={faLayerGroup} className="icon-secondary-article" /> Examples of different versions for the campaign according to the strategy.</li>
            </ul>
            <br />
            <h3>
                <FontAwesomeIcon icon={faArrowDownLong} /> Our team will use the provided assets to build the ads and versions
            </h3>
            <ul>
                <li><FontAwesomeIcon icon={faRulerCombined} className="icon-secondary-article" /> <strong>Size and format specifications</strong> (e.g., 300x250, 728x90, 160x600).</li>
                <li><FontAwesomeIcon icon={faBook} className="icon-secondary-article" /> A guide for <strong>dynamic elements</strong> (e.g., background, title, copies, CTA).</li>
                <li><FontAwesomeIcon icon={faImage} className="icon-secondary-article" /> <strong>Images and graphics</strong> for each different version. Accepted formats are <strong>PNG, JPG, and SVG</strong>.</li>
                <li><FontAwesomeIcon icon={faFileLines} className="icon-secondary-article" /> <strong>Text and copies</strong> for each version, which can be delivered in a separate file (<strong>Word, Excel, Google Sheets</strong>).</li>
                <li><FontAwesomeIcon icon={faHandPointer} className="icon-secondary-article" /> <strong>Interactive elements</strong>, such as a Call-to-Action (<strong>CTA</strong>) or any other button.</li>
                <li><FontAwesomeIcon icon={faLink} className="icon-secondary-article" /> All ad sizes should share the same set of variables, whether they need all of them or not.</li>
            </ul>

            <div className="note">
                <p><strong>Note:</strong>  When providing dynamic elements and text, your specific setup may differ from these guidelines,
                    but you can use this information to begin setting up dynamic variables.</p>
                <ul>
                    <li className="bullet-item">Keep variable names intuitive, descriptive, and to the point.</li>
                    <li className="bullet-item">A maximum of 250 characters is allowed.</li>
                    <li className="bullet-item">Use camel case and/or underscores to make variable names easier to read.</li>
                    <li className="bullet-item">Avoid spaces and special characters.</li>
                    <li className="bullet-item">The following characters are not allowed: &amp; ! ( ) { } ? / \ : ; + &lt; &gt; @ # $ % ^ *'"`+=,</li>
                    <li className="bullet-item">Maintain a consistent naming structure for your dynamic elements (e.g., <code>Version1_Frame1_Headline_txt_300x250</code>).</li>
                </ul>
            </div>


            <h2 id="all-versions-ready" className="article-section-title">
                <FontAwesomeIcon icon={faCheckCircle} /> All Versions and Sizes Ready
            </h2>
            <p> This section provides an overview of best practices when setting up a DCO campaign when
                you already have all your sizes and versions ready. Your specific setup may differ from
                these guidelines, but you can use this information to begin setting up dynamic variables
                in your environment. For this process, you have two options for sending your assets: </p>

            <h3>
                <FontAwesomeIcon icon={faFolderOpen} /> Option 1: Editable Files (PSD)
            </h3>

            <ul>
                <li><FontAwesomeIcon icon={faFileImage} className="icon-secondary-article" /> One <strong>PSD (Photoshop)</strong> file for each size. All layers must be editable.</li>
                <li><FontAwesomeIcon icon={faListCheck} className="icon-secondary-article" /> All layers must be named correctly (e.g., <code>Background</code>, <code>Product_img</code>, <code>Headline</code>).</li>
                <li><FontAwesomeIcon icon={faStar} className="icon-secondary-article" /> Logos and slogans must be editable or provided as a separate vector file (<strong>AI, EPS, SVG</strong>).</li>
                <li><FontAwesomeIcon icon={faClone} className="icon-secondary-article" /> If the ad contains several frames, it's important to share a clear visual of each frame and keep them organized in separate folders within the PSD file.</li>
                <li><FontAwesomeIcon icon={faFont} className="icon-secondary-article" /> The corporate font file (<strong>TTF, OTF</strong>) is required.</li>
                <li><FontAwesomeIcon icon={faPalette} className="icon-secondary-article" /> Brand colors should be set in <strong>RGB or HEX</strong>.</li>
                <li><FontAwesomeIcon icon={faSliders} className="icon-secondary-article" /> If you have preferred animations, transitions, or effects, directions must be included in your request.</li>
            </ul>

            <h3> <FontAwesomeIcon icon={faFileArchive} /> Option 2: Separate Assets </h3>

            <ul>
                <li><FontAwesomeIcon icon={faFileZipper} className="icon-secondary-article" /> A ZIP folder for each banner size, including all the assets needed for the ad.</li>
                <li>
                    <FontAwesomeIcon icon={faImage} className="icon-secondary-article" /> <strong>Images and graphics:</strong>
                    <ul>
                        <li className="bullet-item">Accepted formats: PNG, JPG, SVG.</li>
                        <li className="bullet-item">A resolution of 72 dpi or higher for the best quality.</li>
                        <li className="bullet-item">Use PNG or SVG for transparency.</li>
                        <li className="bullet-item">Logos and slogans must be editable or provided as a separate vector file (AI, EPS, SVG).</li>
                    </ul>
                </li>
                <li><FontAwesomeIcon icon={faFileLines} className="icon-secondary-article" /><strong>Text and copy:</strong>
                    <ul>
                        <li className="bullet-item">Must be delivered in a separate file (<strong>Word, Excel, Google Sheets</strong>).</li>
                        <li className="bullet-item">Include headlines, secondary copy, CTA, etc.</li>
                        <li className="bullet-item">The corporate font file (<strong>TTF, OTF</strong>) is required.</li>
                    </ul>
                </li>
            </ul>

            <h3><strong>Our team will use the provided assets to build the dynamic banner and create all the required versions. For this, we need you to provide:</strong></h3>
            <ul>
                <li><FontAwesomeIcon icon={faDraftingCompass} className="icon-secondary-article" /> Mockups of different versions for the campaign according to the strategy.</li>
                <li><FontAwesomeIcon icon={faTable} className="icon-secondary-article" /> A spreadsheet with the relation of dynamic elements for creative versions (e.g., <code>Headline1</code>, <code>Background1</code>, <code>Copy1</code>).</li>
                <li><FontAwesomeIcon icon={faLink} className="icon-secondary-article" /> All ad sizes should share the same set of variables, whether they need all of them or not.</li>
                <li><FontAwesomeIcon icon={faFolder} className="icon-secondary-article" /> Recommended folder structure:</li>
                <li style={{ paddingLeft: '40px', color: 'var(--text-light)' }}>
                    /Campaign Name<br />/Mockups<br />/Images<br />/Texts<br />/Logos<br />/Fonts<br />/PSD
                </li>
                <li><FontAwesomeIcon icon={faTag} className="icon-secondary-article" /> Keep variable names intuitive, descriptive, and to the point.</li>
                <li><FontAwesomeIcon icon={faArrowsAltH} className="icon-secondary-article" /> A maximum of <strong>250 characters</strong> is allowed.</li>
                <li><FontAwesomeIcon icon={faCode} className="icon-secondary-article" /> Use camel case and/or underscores to make variable names easier to read.</li>
                <li><FontAwesomeIcon icon={faBan} className="icon-secondary-article" /> Avoid spaces and special characters.</li>
                <li><FontAwesomeIcon icon={faFont} className="icon-secondary-article" /> The following characters are not allowed: &amp; ! ( ) { } ? / \ : ; + &lt; &gt; @ # $ % ^ *'"`+=,</li>
                <li><FontAwesomeIcon icon={faSitemap} className="icon-secondary-article" /> Maintain a consistent naming structure for your dynamic assets (e.g., <code>Version1_Frame1_Headline_txt_300x250</code>).</li>
            </ul>


            <div className="note">
                <p><strong>Note:</strong>  If there is any additional information our team should consider,
                    please include it in your request. After reviewing the request, our team will provide a timeline
                    for revisions and final approval. Once the initial Dynamic Ad is approved, our team will create the
                    different versions and sizes needed for the DCO campaign.</p>
            </div>



            {/* Seção 4 */}
            <h2 id="versions-through-feed" className="article-section-title">
                <FontAwesomeIcon icon={faDatabase} /> Versions Through Feed
            </h2>
            <p>
                This section will outline guidelines for managing creative versions delivered via a dynamic feed. If you have all your assets contained in a data base and prefer to manage the creative versions through your feed, our team need to ensure your data is correctly structured for optimal performance. It's important to consider the following points in order to successfully generate the desired versions.
            </p>

            <h3><strong>Mandatory Fields</strong></h3>
            <p>
                A <strong>unique identifier</strong> is essential for each product or service to use as the key for looking up product information in the Feed. For example, a product SKU for a retail product, or a unique combination of origin and destination for a flight route. This identifier must also be available on the product pages of the advertiser's site for product-level retargeting.
            </p>

            <h3><strong>Recommended Fields</strong></h3>
            <p>There are several other recommended fields for re-targeting campaigns more powerful:</p>

            <ul>
                <li><FontAwesomeIcon icon={faFont} className="icon-secondary-article" /> Name</li>
                <li><FontAwesomeIcon icon={faTag} className="icon-secondary-article" /> Category</li>
                <li><FontAwesomeIcon icon={faLink} className="icon-secondary-article" /> Image URL</li>
                <li><FontAwesomeIcon icon={faListCheck} className="icon-secondary-article" /> Price</li>
                <li><FontAwesomeIcon icon={faStar} className="icon-secondary-article" /> Click Through URL (a Deeplink to product page)</li>
            </ul>

            <h3><strong>Required Source Feed Format</strong></h3>
            <p>
                The data source that gets harvested by the Power Feeds system requires the data to have header fields and to be correctly delimited. Acceptable file types include <strong>.txt, .csv, .tsv, gzip</strong>, and one dimensional <strong>.xml</strong> or <strong>RSS</strong> format <strong>XML</strong> feed (For example, a Google Merchant Center feed).
            </p>

            <p>Other requirements:</p>
            <ul>
                <li className="bullet-item">Must have header information</li>
                <li className="bullet-item">Must be delimited (such as tab, comma, or pipe delimited)</li>
            </ul>

            <h3><strong>Example of CSV feed</strong></h3>
            <p><img src={CsvExample} width="900" height="182" alt="CSV Example" /></p>

            <h3><strong>Example of XML feed</strong></h3>
            <div className="code-block">
                {`<?xml version="1.0" encoding="UTF-8"?>
    <products>
    <product id="444672">
        <title>Girls' Wide Molly Riding Boots</title>
        <url>http://www.landsend.com/products/girls-wide-molly-riding-boots/id_279999</url>
        <category>girls</category>
        <price>69</price>
        <image><![CDATA[http://s7.landsend.com/is/image/LandsEnd/444671_AL14_LF_E4I?$ix_img_v1$&wid=170&hei=255]]></image>
    </product>
    <product id="445508">
        <title>Men's Handsewn Boat Shoes</title>
        <url>http://www.landsend.com/products/mens-handsewn-boat-shoes/id_287767</url>
        <category>mens</category>
        <price>79</price>
        <image><![CDATA[http://s7.landsend.com/is/image/LandsEnd/445508_AK14_FF_DMG?$ix_img_v1$&wid=170&hei=255]]></image>
    </product>
    <product id="446859">
        <title>Women's Petite Chambray Shirt</title>
        <url>http://www.landsend.com/products/womens-long-sleeve-chambray-shirt/id_287150_48</url>
        <category>womens</category>
        <price>59</price>
        <image><![CDATA[http://s7.landsend.com/is/image/LandsEnd/447040_AK14_FF_F82?$ix_img_v1$&wid=170&hei=255]]></image>
    </product>
    </products>`}
            </div>

            <div className="note">
                <p><strong>Note:</strong> Your feed may contain additional data used in display ads, However, no other data is needed for the feed to be set up. Examples of other data are:</p>
                <ul>
                    <li className="bullet-item">Stock level</li>
                    <li className="bullet-item">Retail Price versus Sale Price or Discount Amount</li>
                    <li className="bullet-item">Star or Review Rating</li>
                    <li className="bullet-item">Product Description</li>
                    <li className="bullet-item">Custom Recommendations (a comma-delimited field of product IDs used as recommendations, provided that the product IDs also exist in the feed)</li>
                    <li className="bullet-item">Any other product or service information</li>
                </ul>
            </div>
        </ArticleLayout>
    );
};

export default ArticleDynamicCreative;
